<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();
const store = useStore();

const status = computed(() => store.getters['zohoCrmIntegration/getStatus']);
const uiFlags = computed(() => store.getters['zohoCrmIntegration/getUIFlags']);

const connect = async () => {
  try {
    const url = await store.dispatch('zohoCrmIntegration/getAuthorizeUrl');
    if (!url) throw new Error('no url');
    // eslint-disable-next-line no-console
    console.log('[zoho] opening authorize URL');
    window.open(url, 'zoho_oauth', 'width=720,height=820,noopener=no');
  } catch (e) {
    useAlert(t('ZOHO_CRM.CONFIG_MISSING'));
  }
};

const disconnect = async () => {
  if (!window.confirm(t('ZOHO_CRM.DISCONNECT_CONFIRM'))) return;
  await store.dispatch('zohoCrmIntegration/disconnect');
  useAlert(t('ZOHO_CRM.DISCONNECTED'));
};

const handleOauthMessage = event => {
  const payload = event?.data;
  if (!payload || payload.source !== 'zoho-oauth') return;
  // eslint-disable-next-line no-console
  console.log('[zoho] callback message received:', payload);
  store.dispatch('zohoCrmIntegration/fetchStatus');
  useAlert(
    payload.success ? t('ZOHO_CRM.CONNECTED') : t('ZOHO_CRM.CONNECT_FAILED')
  );
};

onMounted(() => {
  store.dispatch('zohoCrmIntegration/fetchStatus');
  window.addEventListener('message', handleOauthMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleOauthMessage);
});
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl ltr:mr-auto rtl:ml-auto p-6">
    <h1 class="text-xl font-semibold text-n-slate-12">
      {{ t('ZOHO_CRM.TITLE') }}
    </h1>
    <p class="mt-1 text-sm text-n-slate-11">
      {{ t('ZOHO_CRM.DESCRIPTION') }}
    </p>

    <div
      class="mt-6 rounded-xl border border-n-weak bg-n-background p-5 space-y-4"
    >
      <div v-if="uiFlags.isFetchingStatus" class="text-sm text-n-slate-10">
        <span class="i-lucide-loader-2 size-4 animate-spin inline-block mr-2" />
        {{ t('ZOHO_CRM.LOADING') }}
      </div>

      <template v-else>
        <div class="flex items-center gap-3">
          <span
            class="inline-flex size-2.5 rounded-full"
            :class="status.connected ? 'bg-n-emerald-9' : 'bg-n-slate-6'"
          />
          <p class="text-sm font-medium text-n-slate-12">
            {{
              status.connected
                ? t('ZOHO_CRM.STATUS_CONNECTED')
                : t('ZOHO_CRM.STATUS_DISCONNECTED')
            }}
          </p>
        </div>
        <p
          v-if="status.connected && status.expires_at"
          class="text-xs text-n-slate-10"
        >
          {{ t('ZOHO_CRM.EXPIRES_AT', { date: status.expires_at }) }}
        </p>

        <p v-if="!status.configured" class="text-xs text-n-amber-11">
          {{ t('ZOHO_CRM.CONFIG_MISSING') }}
        </p>

        <div class="flex gap-2 pt-2">
          <button
            v-if="!status.connected"
            type="button"
            class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            :disabled="!status.configured"
            @click="connect"
          >
            {{ t('ZOHO_CRM.CONNECT') }}
          </button>
          <button
            v-else
            type="button"
            class="rounded-lg bg-n-ruby-9 px-4 py-2 text-sm font-medium text-white hover:opacity-90"
            @click="disconnect"
          >
            {{ t('ZOHO_CRM.DISCONNECT') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
